import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useOperationalBranchNames } from "../../../../../hooks/useOperationalBranchNames";
import { useAuthStore } from "../../../../../store/useAuthStore";
import { useToast } from "../../../../../hooks/useToast";
import {
  assignCase,
  configureFuneralPlannerBranch,
  getFuneralPlanners,
} from "../../../../../services/dashboard";

type Planner = {
  userId: string;
  displayName: string;
  email?: string;
  branchName?: string;
  activeCaseCount: number;
  lastAssignedAt?: string;
  recommended?: boolean;
};

const responseMessage = (response: any, fallback: string) =>
  response?.data?.message || response?.data?.error || fallback;

export const PlannerBranchSetupDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { userId } = useAuthStore();
  const { branchOptions, loading: branchesLoading } = useOperationalBranchNames("MORTUARY");
  const { showErrorToast, showSuccessToast } = useToast();
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const mappedCount = planners.filter((planner) => planner.branchName).length;

  const loadPlanners = async () => {
    setLoading(true);
    const response = await getFuneralPlanners();
    if (response?.status === 200) {
      const rows = Array.isArray(response.data) ? response.data : [];
      setPlanners(rows);
      setSelections(
        rows.reduce((result: Record<string, string>, planner: Planner) => {
          result[planner.userId] = planner.branchName || "";
          return result;
        }, {}),
      );
    } else {
      showErrorToast(responseMessage(response, "Unable to load Funeral Planners"));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) void loadPlanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const saveBranch = async (planner: Planner) => {
    const branchName = selections[planner.userId];
    if (!branchName || !userId) {
      showErrorToast("Select a branch before saving");
      return;
    }
    setSavingId(planner.userId);
    const response = await configureFuneralPlannerBranch(planner.userId, {
      branchName,
      configuredByUserId: userId,
    });
    if (response?.status === 200) {
      showSuccessToast(`${planner.displayName} is now associated with ${branchName}`);
      await loadPlanners();
    } else {
      showErrorToast(responseMessage(response, "Unable to save planner branch"));
    }
    setSavingId("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", sm: "min(860px, calc(100% - 48px))" },
          maxHeight: "min(760px, calc(100vh - 32px))",
          m: { xs: 1.5, sm: 3 },
          borderRadius: { xs: 3, sm: 4 },
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.25)",
        },
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(3px)", bgcolor: "rgba(15, 23, 42, 0.48)" } } }}
    >
      <Box sx={{ height: 5, bgcolor: "#F36C00" }} />
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          py: 2.25,
          background: "linear-gradient(135deg, #FFF8F2 0%, #FFFFFF 68%)",
          borderBottom: "1px solid #F1E7DE",
        }}
      >
        <Avatar sx={{ width: 44, height: 44, bgcolor: "#FFF0E3", color: "#E86100" }}>
          <AccountTreeRoundedIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 21 }, color: "#18202D" }}>
            Planner Mortuary Setup
          </Typography>
          <Typography sx={{ mt: 0.35, color: "#667085", fontSize: 13.5, lineHeight: 1.5 }}>
            Link each Funeral Planner to an active IFAS mortuary.
          </Typography>
        </Box>
        <IconButton aria-label="Close planner mortuary setup" onClick={onClose} size="small" sx={{ color: "#667085" }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      {loading && <LinearProgress sx={{ bgcolor: "#FFF0E3", "& .MuiLinearProgress-bar": { bgcolor: "#F36C00" } }} />}

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip
            size="small"
            icon={<CheckCircleRoundedIcon />}
            label={`${mappedCount} mapped`}
            sx={{ bgcolor: "#ECFDF3", color: "#027A48", fontWeight: 700, "& .MuiChip-icon": { color: "#12B76A" } }}
          />
          <Chip
            size="small"
            label={`${Math.max(planners.length - mappedCount, 0)} unassigned`}
            sx={{ bgcolor: "#FFF4E8", color: "#B54708", fontWeight: 700 }}
          />
          <Typography sx={{ ml: { sm: "auto" }, color: "#98A2B3", fontSize: 12.5 }}>
            {planners.length} active planner{planners.length === 1 ? "" : "s"}
          </Typography>
        </Box>

        <Box sx={{ overflowX: "auto", border: "1px solid #E4E7EC", borderRadius: 3, bgcolor: "#FFF" }}>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                <TableCell sx={{ py: 1.35, color: "#475467", fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.45 }}>
                  Funeral Planner
                </TableCell>
                <TableCell sx={{ py: 1.35, color: "#475467", fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.45 }}>
                  Mortuary
                </TableCell>
                <TableCell align="right" sx={{ py: 1.35, color: "#475467", fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.45 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planners.map((planner) => {
                const selectedBranch = selections[planner.userId] || "";
                const isChanged = selectedBranch !== (planner.branchName || "");
                const isSaving = savingId === planner.userId;
                const initials = planner.displayName
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part[0])
                  .join("")
                  .toUpperCase();

                return (
                  <TableRow key={planner.userId} sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover": { bgcolor: "#FFFCF9" } }}>
                    <TableCell sx={{ py: 1.6 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: "#FFF0E3", color: "#D95D00", fontSize: 13, fontWeight: 800 }}>
                          {initials || "FP"}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 750, color: "#1D2939", fontSize: 14, textTransform: "capitalize" }}>
                            {planner.displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", color: "#667085", maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {planner.email || "No email available"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.6, minWidth: 270 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={selectedBranch}
                          disabled={branchesLoading || isSaving}
                          onChange={(event) =>
                            setSelections((current) => ({
                              ...current,
                              [planner.userId]: String(event.target.value),
                            }))
                          }
                          renderValue={(value) => value || "Select mortuary"}
                          sx={{
                            borderRadius: 2,
                            bgcolor: "#FFF",
                            color: selectedBranch ? "#1D2939" : "#98A2B3",
                            "& .MuiSelect-select": { py: 1.15, fontWeight: selectedBranch ? 700 : 500 },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#F36C00" },
                          }}
                        >
                          {branchOptions.map((branch) => (
                            <MenuItem key={branch} value={branch}>{branch}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.6 }}>
                      <Button
                        variant={isChanged ? "contained" : "text"}
                        disabled={!selectedBranch || !isChanged || isSaving}
                        onClick={() => saveBranch(planner)}
                        sx={{
                          minWidth: 106,
                          borderRadius: 2,
                          boxShadow: "none",
                          fontWeight: 800,
                          bgcolor: isChanged ? "#F36C00" : undefined,
                          "&:hover": { bgcolor: isChanged ? "#D95D00" : undefined, boxShadow: "none" },
                          "&.Mui-disabled": { color: selectedBranch && !isChanged ? "#027A48" : "#98A2B3" },
                        }}
                      >
                        {isSaving ? "Saving..." : selectedBranch && !isChanged ? "Assigned" : "Save branch"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && planners.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    <AccountTreeRoundedIcon sx={{ color: "#D0D5DD", fontSize: 36, mb: 0.75 }} />
                    <Typography sx={{ color: "#344054", fontWeight: 700 }}>No Funeral Planners found</Typography>
                    <Typography sx={{ color: "#98A2B3", fontSize: 13, mt: 0.25 }}>
                      Active Funeral Planner users will appear here for mortuary setup.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.75, borderTop: "1px solid #EAECF0", bgcolor: "#FCFCFD" }}>
        <Typography sx={{ mr: "auto", color: "#667085", fontSize: 12.5, display: { xs: "none", sm: "block" } }}>
          Changes apply to future case assignments.
        </Typography>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ minWidth: 96, borderRadius: 2, bgcolor: "#20262E", boxShadow: "none", fontWeight: 800, "&:hover": { bgcolor: "#101828", boxShadow: "none" } }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AssignPlannerDialog = ({
  open,
  caseItem,
  onClose,
  onAssigned,
}: {
  open: boolean;
  caseItem: any;
  onClose: () => void;
  onAssigned: () => void;
}) => {
  const { userId } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [selectedPlannerId, setSelectedPlannerId] = useState("");
  const [loading, setLoading] = useState(false);
  const branchName = caseItem?.mortuaryName || caseItem?.branchName || "";

  useEffect(() => {
    if (!open || !branchName) return;
    setLoading(true);
    void getFuneralPlanners(branchName).then((response: any) => {
      if (response?.status === 200) {
        const rows: Planner[] = Array.isArray(response.data) ? response.data : [];
        setPlanners(rows);
        setSelectedPlannerId(rows.find((planner) => planner.recommended)?.userId || "");
      } else {
        showErrorToast(responseMessage(response, "Unable to load mortuary Funeral Planners"));
      }
      setLoading(false);
    });
    // Toast helpers are intentionally omitted because the hook returns new
    // function references on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchName, open]);

  const selectedPlanner = useMemo(
    () => planners.find((planner) => planner.userId === selectedPlannerId),
    [planners, selectedPlannerId],
  );

  const submitAssignment = async () => {
    if (!selectedPlannerId || !userId || !caseItem?.caseID) return;
    setLoading(true);
    const response = await assignCase({
      case_id: caseItem.caseID,
      assigned_to_user_id: selectedPlannerId,
      assigned_by_user_id: userId,
    });
    if (response?.status === 200) {
      showSuccessToast(`Case assigned to ${selectedPlanner?.displayName || "the selected planner"}`);
      onAssigned();
      onClose();
    } else {
      showErrorToast(responseMessage(response, "Unable to assign case"));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Assign Funeral Planner</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" mb={2}>
          Case mortuary: <strong>{branchName || "Not configured"}</strong>
        </Typography>
        {!branchName && (
          <Typography color="error">This case has no branch/mortuary and cannot be assigned.</Typography>
        )}
        {branchName && planners.map((planner) => (
          <Box
            key={planner.userId}
            onClick={() => setSelectedPlannerId(planner.userId)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              mb: 1,
              border: "1px solid",
              borderColor: selectedPlannerId === planner.userId ? "primary.main" : "#E5E7EB",
              borderRadius: 2,
              cursor: "pointer",
            }}
          >
            <Radio checked={selectedPlannerId === planner.userId} />
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography fontWeight={750}>{planner.displayName}</Typography>
                {planner.recommended && <Chip label="Recommended" color="success" size="small" />}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {planner.activeCaseCount} active cases
                {planner.lastAssignedAt
                  ? ` • Last assigned ${new Date(planner.lastAssignedAt).toLocaleDateString()}`
                  : " • No previous assignment"}
              </Typography>
            </Box>
          </Box>
        ))}
        {!loading && branchName && planners.length === 0 && (
          <Typography color="text.secondary">
            No Funeral Planner is configured for this branch. Configure a planner branch first.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button
          onClick={submitAssignment}
          variant="contained"
          disabled={loading || !selectedPlannerId}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};
